import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useUiStore } from "@/stores/UiStore";

const stepContent = [
  {
    title: "🎉 Introducing Journeys!",
    description:
      "Meet Journeys — your new space to organize and manage your bidding process with ease. Check the interactive demo below to see how it works.",
  },
  {
    title: "Organize Your Bidding Process",
    description:
      "Journey Hub is designed to help you manage your bidding process efficiently. It’s your one-stop shop for everything related to your bids.",
    image: "/feature/journey/journey-hub.png",
  },
  {
    title: "What’s a Journey?",
    description:
      "A Journey is created when you decide to bid on an opportunity. It helps you stay focused by tracking everything from your initial assessment to final submission. Inside a Journey, you can add tasks, chat with SamSearch AI, generate proposals, and more.",
    image: "/feature/journey/journey.png",
  },
  {
    title: "Starting a Journey",
    description:
      'Click the new "Start Journey" button on any opportunity, or go to the "Journey Hub" tab to create one manually.',
    image: "/feature/journey/start-journey.png",
  },
  {
    title: "Tasks in Your Journey",
    description:
      "Tasks help you break down your work. You can create them, set deadlines, and keep track of progress easily.",
    image: "/feature/journey/tasks.png",
  },
  {
    title: "See It All in the Calendar",
    description:
      "Use the calendar view to see all your tasks and deadlines in one place — available in both the Journey Hub and the tasks tab.",
    image: "/feature/journey/calendar.png",
  },
];

function ShowOnceJourneyDialog() {
  const name = "journey-2025-q2";
  const hasSeen = useUiStore((s) => s.hasSeenFeature(name));
  const acknowledge = useUiStore((s) => s.acknowledgeFeature);

  const [step, setStep] = useState(1);
  if (hasSeen) {
    return null;
  }

  const totalSteps = stepContent.length;

  const handleContinue = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <Dialog
      defaultOpen={true}
      onOpenChange={(open) => {
        if (open) setStep(1);
        else acknowledge(name);
      }}
    >
      <DialogContent className="max-w-[75vw] max-h-[90vh] overflow-y-auto gap-0">
        <div className="space-y-6 px-6 pb-6 pt-3">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {stepContent[step - 1].title}
            </DialogTitle>
            <DialogDescription>
              {stepContent[step - 1].description}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex justify-center space-x-1.5 max-sm:order-1">
              {[...Array(totalSteps)].map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-1.5 w-1.5 rounded-full bg-primary",
                    index + 1 === step ? "bg-primary" : "opacity-20"
                  )}
                />
              ))}
            </div>
            <DialogFooter>
              {step > 1 && (
                <Button type="button" variant="ghost" onClick={handleBack}>
                  Back
                </Button>
              )}
              {step < totalSteps ? (
                <Button
                  className="group"
                  type="button"
                  onClick={handleContinue}
                >
                  Next
                  <ArrowRight
                    className="-me-1 ms-2 opacity-60 transition-transform group-hover:translate-x-0.5"
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </Button>
              ) : (
                <DialogClose asChild>
                  <Button type="button">Let’s Start the Journey!</Button>
                </DialogClose>
              )}
            </DialogFooter>
          </div>
        </div>
        <div className="p-2">
          {stepContent[step - 1]?.image ? (
            <img
              className="w-full rounded-lg object-cover border-2 border-[var(--sky-200)]"
              src={stepContent[step - 1].image}
              width={382}
              height={216}
              alt="dialog"
            />
          ) : (
            <iframe
              src="https://app.supademo.com/embed/cm9ozscat62nfljv5353npxxc?embed_v=2"
              loading="lazy"
              title="SamSearch Journey Hub Demo"
              allow="clipboard-write"
              frameborder="0"
              allowfullscreen=""
              height={522}
              className="w-full rounded-lg"
            ></iframe>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ShowOnceJourneyDialog };
